import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { adminAPI } from '../../../services/authService';
import { confirmDialog } from '../../../utils/confirmDialog';
import { notifyError, notifySuccess } from '../../../utils/notify';

const HIDE_SUBMITTAL = true;
const PROTECTED = new Set(['SUPER_ADMIN', 'ADMIN']);

const PAGE_LABELS = {
  'page:reports_dashboard': 'Reports dashboard',
  'page:summary': 'Summary',
  'page:correspondence': 'Correspondence reports',
  'page:financials': 'Financials & BG',
  'page:technical': 'Technical & Clearance',
  'page:drawings': 'Drawings & Design',
  'page:senior_management': 'Senior Management MI (company financials)',
  'page:custom_report_builder': 'Custom Report Builder',
  'page:saved_custom_reports': 'Saved Custom Reports',
};

const actionLabel = (key) => {
  if (key === 'update') return 'Edit';
  if (PAGE_LABELS[key]) return PAGE_LABELS[key];
  if (key.startsWith('page:')) return key.replace(/^page:/, '').replace(/[-_]/g, ' ');
  return key.replace(/_/g, ' ');
};

const emptyRole = () => ({ name: '', code: '', description: '', status: 'Active', permissionIds: [] });

const UsersAccessRoles = ({ initialMode }) => {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [creating, setCreating] = useState(initialMode === 'create');
  const [form, setForm] = useState(emptyRole());
  const [expanded, setExpanded] = useState({});
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [preservedHiddenIds, setPreservedHiddenIds] = useState([]);

  const load = useCallback(async () => {
    const [r, p] = await Promise.all([adminAPI.getRoles(), adminAPI.getPermissions()]);
    const roleList = r?.data?.data || r?.data || [];
    let perms = p?.data?.data || p?.data || [];
    if (HIDE_SUBMITTAL) perms = perms.filter((x) => x.moduleKey !== 'submittal');
    setRoles(roleList);
    setPermissions(perms);
    if (!selectedId && roleList[0]) setSelectedId(roleList[0].id);
  }, [selectedId]);

  useEffect(() => { load().catch(() => notifyError('Unable to load roles')); }, []); // eslint-disable-line

  const modules = useMemo(() => {
    const map = new Map();
    permissions.forEach((p) => {
      if (!map.has(p.moduleKey)) map.set(p.moduleKey, []);
      map.get(p.moduleKey).push(p);
    });
    return Array.from(map.entries()).map(([moduleKey, items]) => ({
      moduleKey,
      actions: items.filter((x) => !String(x.actionKey).startsWith('page:')),
      pages: items.filter((x) => String(x.actionKey).startsWith('page:')),
      all: items,
    }));
  }, [permissions]);

  const selected = roles.find((r) => r.id === selectedId);

  const startCreate = () => {
    setCreating(true);
    setForm(emptyRole());
    setPreservedHiddenIds([]);
    setExpanded({});
  };

  const loadRoleIntoForm = async (role) => {
    setCreating(false);
    setSelectedId(role.id);
    const full = roles.find((r) => r.id === role.id) || role;
    const allIds = (full.rolePermissions || full.permissions || [])
      .map((x) => x.permissionId || x.permission?.id || x.id)
      .filter(Boolean);
    const visibleIds = allIds.filter((id) => permissions.some((p) => p.id === id));
    // Keep grants for modules hidden from UI (e.g. submittal) so Save does not strip them
    setPreservedHiddenIds(allIds.filter((id) => !permissions.some((p) => p.id === id)));
    setForm({
      name: full.name || '',
      code: full.code || '',
      description: full.description || '',
      status: full.status || 'Active',
      permissionIds: visibleIds,
    });
  };

  useEffect(() => {
    if (selected && !creating) loadRoleIntoForm(selected);
  }, [selectedId, roles]); // eslint-disable-line

  const ensureView = (moduleKey, nextIds) => {
    const view = permissions.find((p) => p.moduleKey === moduleKey && p.actionKey === 'view');
    if (view && !nextIds.includes(view.id)) nextIds.push(view.id);
    return nextIds;
  };

  const togglePerm = (perm) => {
    setForm((prev) => {
      let ids = [...prev.permissionIds];
      const has = ids.includes(perm.id);
      if (has) {
        if (perm.actionKey === 'view') {
          // clear module
          const moduleIds = permissions.filter((p) => p.moduleKey === perm.moduleKey).map((p) => p.id);
          ids = ids.filter((id) => !moduleIds.includes(id));
        } else {
          ids = ids.filter((id) => id !== perm.id);
        }
      } else {
        ids.push(perm.id);
        if (perm.actionKey !== 'view') ids = ensureView(perm.moduleKey, ids);
      }
      return { ...prev, permissionIds: ids };
    });
  };

  const selectAllModule = (mod) => {
    setForm((prev) => {
      const ids = new Set(prev.permissionIds);
      mod.all.forEach((p) => ids.add(p.id));
      return { ...prev, permissionIds: Array.from(ids) };
    });
  };

  const clearModule = (mod) => {
    const remove = new Set(mod.all.map((p) => p.id));
    setForm((prev) => ({ ...prev, permissionIds: prev.permissionIds.filter((id) => !remove.has(id)) }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const code = String(form.code || '').trim().toUpperCase().replace(/\s+/g, '_');
      if (!form.name || !code) {
        notifyError('Role name and code are required');
        setSaving(false);
        return;
      }
      let roleId = selectedId;
      if (creating) {
        const created = await adminAPI.createRole({
          name: form.name,
          code,
          description: form.description,
          status: form.status,
        });
        roleId = created?.data?.id || created?.data?.data?.id;
      } else {
        if (PROTECTED.has(String(selected?.code || '').toUpperCase()) && code !== selected.code) {
          notifyError('Protected role code cannot be changed');
          setSaving(false);
          return;
        }
        await adminAPI.updateRole(selectedId, {
          name: form.name,
          code,
          description: form.description,
          status: form.status,
        });
        roleId = selectedId;
      }
      const mergedIds = Array.from(new Set([...(form.permissionIds || []), ...preservedHiddenIds]));
      await adminAPI.updateRolePermissions(roleId, mergedIds);
      notifySuccess(creating ? 'System Role created' : 'System Role saved');
      setCreating(false);
      await load();
      setSelectedId(roleId);
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Unable to save role');
    } finally {
      setSaving(false);
    }
  };

  const duplicateRole = async () => {
    if (!selected) return;
    setCreating(true);
    setForm({
      name: `${selected.name} Copy`,
      code: `${selected.code}_COPY`,
      description: selected.description || '',
      status: 'Active',
      permissionIds: [...form.permissionIds],
    });
  };

  const deleteRole = async () => {
    if (!selected) return;
    if (PROTECTED.has(String(selected.code || '').toUpperCase())) {
      notifyError('Protected roles cannot be deleted');
      return;
    }
    if (!(await confirmDialog(`Delete role ${selected.code}?`, 'Delete System Role'))) return;
    try {
      await adminAPI.deleteRole(selected.id);
      notifySuccess('Role deleted');
      setSelectedId(null);
      await load();
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Delete failed');
    }
  };

  const filteredRoles = roles.filter((r) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return [r.name, r.code, r.description].some((x) => String(x || '').toLowerCase().includes(q));
  });

  const review = useMemo(() => {
    const selectedPerms = permissions.filter((p) => form.permissionIds.includes(p.id));
    const byModule = {};
    selectedPerms.forEach((p) => {
      if (!byModule[p.moduleKey]) byModule[p.moduleKey] = { actions: [], pages: [] };
      if (String(p.actionKey).startsWith('page:')) byModule[p.moduleKey].pages.push(p.actionKey);
      else byModule[p.moduleKey].actions.push(p.actionKey);
    });
    const warnings = [];
    if (selectedPerms.some((p) => p.moduleKey === 'financials' && p.actionKey === 'delete')) {
      warnings.push('This System Role can delete financial records.');
    }
    if (selectedPerms.some((p) => p.moduleKey === 'admin_panel' && p.actionKey === 'manage')) {
      warnings.push('This System Role has full Admin Panel manage access.');
    }
    return { byModule, total: selectedPerms.length, warnings };
  }, [form.permissionIds, permissions]);

  return (
    <div>
      <div className="filter-bar">
        <input placeholder="Search roles" value={search} onChange={(e) => setSearch(e.target.value)} />
        <button type="button" className="btn-primary" onClick={startCreate}>Create System Role</button>
      </div>
      <div className="ua-two-pane">
        <div className="panel ua-panel">
          <h3 style={{ marginTop: 0 }}>System Roles</h3>
          <div className="ua-role-list">
            {filteredRoles.map((r) => (
              <button
                key={r.id}
                type="button"
                className={`ua-role-item ${!creating && selectedId === r.id ? 'is-active' : ''}`}
                onClick={() => loadRoleIntoForm(r)}
              >
                <div style={{ fontWeight: 600 }}>{r.name}</div>
                <div style={{ fontSize: 12, color: '#64748b' }}>{r.code} · {r.status}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="panel ua-panel">
          <h3 style={{ marginTop: 0 }}>{creating ? 'Create System Role' : 'Role details & permissions'}</h3>
          <div className="form-row">
            <div className="form-group"><label>Role Name</label><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="form-group"><label>Role Code</label><input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} disabled={!creating && PROTECTED.has(String(selected?.code || '').toUpperCase())} /></div>
          </div>
          <div className="form-group"><label>Description</label><input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          <div className="form-group">
            <label>Status</label>
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>

          <h4>Module-wise permissions</h4>
          {modules.map((mod) => {
            const selectedCount = mod.all.filter((p) => form.permissionIds.includes(p.id)).length;
            const open = expanded[mod.moduleKey] !== false;
            return (
              <div className="ua-module-card" key={mod.moduleKey}>
                <button type="button" className="ua-module-card__head" onClick={() => setExpanded((e) => ({ ...e, [mod.moduleKey]: !open }))}>
                  <span>{open ? '▼' : '▶'} {mod.moduleKey.replace(/_/g, ' ')}</span>
                  <span style={{ fontSize: 12, color: '#64748b' }}>{selectedCount} of {mod.all.length} selected</span>
                </button>
                {open && (
                  <div className="ua-module-card__body">
                    <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                      <button type="button" className="btn-secondary btn-sm" onClick={() => selectAllModule(mod)}>Select All</button>
                      <button type="button" className="btn-secondary btn-sm" onClick={() => clearModule(mod)}>Clear Module</button>
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 6 }}>Actions</div>
                    <div className="ua-check-grid">
                      {mod.actions.map((p) => (
                        <label key={p.id}>
                          <input type="checkbox" checked={form.permissionIds.includes(p.id)} onChange={() => togglePerm(p)} />
                          {actionLabel(p.actionKey)}
                        </label>
                      ))}
                    </div>
                    {mod.pages.length > 0 && (
                      <>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', margin: '12px 0 6px' }}>Page Access</div>
                        <div className="ua-check-grid">
                          {mod.pages.map((p) => (
                            <label key={p.id}>
                              <input type="checkbox" checked={form.permissionIds.includes(p.id)} onChange={() => togglePerm(p)} />
                              {actionLabel(p.actionKey)}
                            </label>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          <h4>Effective access review</h4>
          <p style={{ fontSize: 13 }}>Total permissions: <strong>{review.total}</strong></p>
          {review.warnings.map((w) => <p key={w} style={{ color: '#b45309', fontSize: 13 }}>{w}</p>)}
          <ul style={{ fontSize: 13 }}>
            {Object.entries(review.byModule).map(([mod, info]) => (
              <li key={mod}><strong>{mod}</strong>: {info.actions.join(', ') || '—'}{info.pages.length ? ` · Pages: ${info.pages.length}` : ''}</li>
            ))}
          </ul>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
            <button type="button" className="btn-primary" disabled={saving} onClick={save}>
              {creating ? 'Create Role & Save Permissions' : 'Save Role & Permissions'}
            </button>
            {!creating && selected && (
              <>
                <button type="button" className="btn-secondary" onClick={duplicateRole}>Duplicate</button>
                <button type="button" className="btn-danger" onClick={deleteRole}>Delete</button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UsersAccessRoles;
